import { registerComponent } from '../../../lib/vulcan-lib/components';
import React, { useState } from 'react';
import { useNewEvents } from '../../../lib/events/withNewEvents';
import { useCurrentUser } from '../../common/withUser';
import { truncatise } from '../../../lib/truncatise';
import Edit from '@/lib/vendor/@material-ui/icons/src/Edit';
import { userCanModeratePost } from '../../../lib/collections/users/helpers';
import { useDialog } from '../../common/withDialog'
import withErrorBoundary from '../../common/withErrorBoundary'
import { getFrontpageGuidelines, getDefaultGuidelines } from './ForumModerationGuidelinesContent'
import { userCanModerateSubforum } from '../../../lib/collections/tags/helpers';
import { useQuery } from "@/lib/crud/useQuery";
import { gql } from "@/lib/generated/gql-codegen";
import { TooltipSpan } from '@/components/common/FMTooltip';
import ModerationGuidelinesEditForm from "./ModerationGuidelinesEditForm";
import ContentStyles from "../../common/ContentStyles";
import { defineStyles, safeForDarkMode } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const PostsModerationGuidelinesQuery = gql(`
  query PostsModerationGuidelines($documentId: String) {
    PostsModerationGuidelines: post(selector: { documentId: $documentId }) {
      result {
        ...PostsModerationGuidelines
      }
      __typename
    }
  }
`)

const TagModerationGuidelinesQuery = gql(`
  query TagModerationGuidelines($documentId: String) {
    TagModerationGuidelines: tag(selector: { documentId: $documentId }) {
      result {
        ...TagFragment
      }
      __typename
    }
  }
`)

const styles = defineStyles('ModerationGuidelinesBox', (theme: ThemeType) => ({
  root: {
    padding: 16,
    position: "relative"
  },
  assistance: { //UNUSED
    color: theme.palette.text.normal,
  },
  'easy-going': {
    color: safeForDarkMode('rgba(100, 169, 105, 0.9)'),
  },
  'norm-enforcing': {
    color: safeForDarkMode('#2B6A99'),
  },
  'reign-of-terror': {
    color: safeForDarkMode('rgba(179,90,49,.8)'),
  },
  editButtonWrapper: {
    cursor: "pointer",
    position: 'absolute',
    right: 16,
  },
  editButton: {
    height: '0.8em'
  },
  collapse: {
    display: "flex",
    justifyContent: "flex-end",
    fontSize: 14,
    marginBottom: 4,
  },
  moderationGuidelines: {
    fontSize: "1.1rem",
    '& p, & ul': {
      marginTop: '.6em',
      marginBottom: '.6em'
    },
    '& li': {
      marginTop: '.4em',
      marginBottom: '.4em'
    }
  }
}))

const truncateGuidelines = (guidelines: string) => {
  return truncatise(guidelines, {
    TruncateLength: 300,
    TruncateBy: "characters",
    Suffix: `... <a>Read More</a>`,
    Strict: false
  });
}

// Build the header as JSX (so React escapes the user-controlled displayName)
// and return the rest as an HTML string. The body is composed only of trusted
// HTML — server-sanitized `post.moderationGuidelines.html` and the hardcoded
// frontpage/default fallbacks — so it can safely flow to dangerouslySetInnerHTML.
interface ModerationGuidelinesParts {
  header: React.ReactNode | null;
  bodyHtml: string;
  truncatedBodyHtml: string;
}

const getPostModerationGuidelines = (
  post: PostsModerationGuidelines,
  classes: ClassesType<typeof styles>,
): ModerationGuidelinesParts => {
  const moderationStyle = post.moderationStyle || (post.user?.moderationStyle || "")
  const moderationStyleClass = (classes as AnyBecauseTodo)[moderationStyle];

  const { html = "" } = post.moderationGuidelines || {}
  const showHeader = !!(post.user && (html || moderationStyle));
  const header = showHeader ? (
    <>
      <p><em>{post.user!.displayName}{"'s commenting guidelines"}</em></p>
      <p className={moderationStyleClass}>{moderationStyleLookup[moderationStyle] ?? ""}</p>
    </>
  ) : null;

  let bodyHtml = "";
  if (html) bodyHtml += html;
  if (html && post.frontpageDate) bodyHtml += "<hr/>";
  if (post.frontpageDate) {
    bodyHtml += getFrontpageGuidelines();
  } else if (!html && !moderationStyle) {
    bodyHtml += getDefaultGuidelines();
  }

  return { header, bodyHtml, truncatedBodyHtml: truncateGuidelines(bodyHtml) };
}

const getSubforumModerationGuidelines = (tag: TagFragment): ModerationGuidelinesParts => {
  const { html } = tag.moderationGuidelines || {}
  const bodyHtml = html ?? ''
  return { header: null, bodyHtml, truncatedBodyHtml: truncateGuidelines(bodyHtml) }
}

const ModerationGuidelinesBox = ({commentType = "post", documentId}: {
  commentType?: "post" | "subforum",
  documentId: string,
}) => {
  const classes = useStyles(styles);
  const { recordEvent } = useNewEvents();
  const currentUser = useCurrentUser();
  const { openDialog } = useDialog();
  const [expanded, setExpanded] = useState(false)
  const isPost = commentType === "post"

  const { data: post, loading } = useQuery(PostsModerationGuidelinesQuery, {
    variables: { documentId },
    skip: !documentId || !isPost,
    fetchPolicy: "cache-first",
  });

  const { data: tag, loading: tagLoading } = useQuery(TagModerationGuidelinesQuery, {
    variables: { documentId },
    skip: !documentId || isPost,
    fetchPolicy: "cache-first",
  });

  const document = isPost ? post?.PostsModerationGuidelines?.result : tag?.TagModerationGuidelines?.result;

  const isPostType = (
    document: PostsModerationGuidelines | TagFragment,
  ): document is PostsModerationGuidelines => isPost && !!document;

  if (!document || loading || tagLoading) return null

  const handleClick = (e: React.MouseEvent) => {
    // On click, toggle moderation-guidelines expansion. Event handler is only
    // attached if they're long enough for expand/collapse to be a thing. Note
    // that if the moderation guidelines contain a link, this could also be a
    // link-click, in which case it's important not to preventDefault or
    // stopPropagation.

    // Only toggle moderation-guidelines expansion on an unmodified left-click
    // (ie not if this is an open-new-tab on a link)
    if (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey || e.button !== 0) {
      return;
    }

    setExpanded(!expanded)

    if (currentUser) {
      const eventProperties = {
        important: false,
        intercom: true,
        documentId: document?.userId,
        commentType: commentType,
        targetState: !expanded
      };
      recordEvent('toggled-user-moderation-guidelines', false, eventProperties);
    }
  }

  const openEditDialog = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    openDialog({
      name: "ModerationGuidelinesEditForm",
      contents: ({ onClose }) => <ModerationGuidelinesEditForm
        onClose={onClose}
        commentType={commentType}
        documentId={documentId}
      />
    });
  }

  const { header, bodyHtml, truncatedBodyHtml } = isPostType(document) ? getPostModerationGuidelines(document, classes) : getSubforumModerationGuidelines(document)
  const displayedBodyHtml = expanded ? bodyHtml : truncatedBodyHtml

  const expandable = bodyHtml.trim().length !== truncatedBodyHtml.trim().length

  return (
    <div className={classes.root} onClick={expandable ? handleClick : undefined}>
      {
        !!(isPostType(document) ? userCanModeratePost(currentUser, document) : userCanModerateSubforum(currentUser, document)) &&
        <span onClick={openEditDialog}>
          <TooltipSpan
            title="Edit moderation guidelines"
            className={classes.editButtonWrapper}
          >
            <Edit className={classes.editButton}/>
          </TooltipSpan>
        </span>
      }
      <ContentStyles contentType="comment" className={classes.moderationGuidelines}>
        {header}
        <div dangerouslySetInnerHTML={{__html: displayedBodyHtml}}/>
        {expanded && expandable && <a className={classes.collapse}>(Click to Collapse)</a>}
      </ContentStyles>
    </div>
  )
}

const moderationStyleLookup: Partial<Record<string, string>> = {
  'norm-enforcing': "Norm Enforcing - I try to enforce particular rules",
  'reign-of-terror': "Reign of Terror - I delete anything I judge to be counterproductive",
  'easy-going': "Easy Going - I just delete obvious spam and trolling."
}

export default registerComponent('ModerationGuidelinesBox', ModerationGuidelinesBox, {
  hocs: [withErrorBoundary]
});


